import type { IconName } from '../components/icons';
import type { PanelLang } from './lang';

/**
 * Bản đồ điều hướng của panel — DỮ LIỆU THUẦN, không JSX.
 *
 * Tách khỏi `components/Layout.tsx` ngày 13/08/2026, khi trang category ra đời:
 * ba chỗ cùng cần bảng này (thanh bên, trang category, lưới ở trang Tổng quan),
 * và import chúng từ một component là mời một vòng phụ thuộc. File này không có
 * runtime import nào — CẢ HAI dòng import trên đều là `import type` và bị xoá
 * hẳn lúc biên dịch — nên `nav.test.ts` đọc được nó bằng `node --test` mà không
 * cần DOM.
 *
 * Dòng `IconName` là dòng dễ hỏng nhất trong file: `components/icons.tsx` có
 * JSX và kéo theo React. Bỏ chữ `type` đi thì `node --test` phải nạp React và
 * test frontend duy nhất của kho chết ngay ở dòng import.
 */

/** Nhãn hai thứ tiếng, khai ngay tại chỗ dùng — xem `lib/lang.tsx`. */
export type NavLabel = Record<PanelLang, string>;

/** Discord ID duy nhất được quản lý cấu hình DM tự động. */
export const DIRECT_MESSAGE_ADMIN_ID = '1431484011179544618';

export interface NavItem {
  readonly to: string;
  readonly label: NavLabel;
  /**
   * Một câu nói trang này làm gì — chữ trên thẻ ở trang category.
   *
   * Bắt buộc, và `nav.test.ts` chốt điều đó. Một thẻ chỉ có tiêu đề thì trang
   * category không hơn gì cái menu cũ; câu mô tả mới là thứ trả lời được "tôi
   * cần vào đâu" cho người chưa thuộc bản đồ.
   */
  readonly description: NavLabel;
  /** true = chỉ Quản trị viên mới thấy mục này. */
  readonly adminOnly?: boolean;
  /**
   * true = chỉ CHỦ BOT (`BOT_OWNER_IDS`) thấy mục này.
   *
   * Chặt hơn `adminOnly` một bậc, và dành cho đúng một thứ: trang thao tác trên
   * khoá API do chủ bot trả tiền. Panel này phục vụ quản trị viên của nhiều cộng
   * đồng khác nhau, nên một mục như thế hiện ra cho tất cả họ vừa vô dụng vừa là
   * lời mời đi hỏi tại sao mình bấm vào thì báo lỗi.
   *
   * Ẩn mục KHÔNG phải là lớp bảo vệ — `requireBotOwner` phía máy chủ mới là.
   */
  readonly ownerOnly?: boolean;
  /** Chỉ các Discord ID trong danh sách mới thấy mục này. */
  readonly allowedUserIds?: readonly string[];
  /**
   * true = trang CẤU HÌNH của nhóm, không phải chỗ làm việc hằng ngày.
   *
   * Chúng đứng cuối nhóm sau một vạch ngăn. Đây là thứ thay cho việc gom hết
   * cấu hình vào một nhóm "Hệ thống": cấu hình collab thuộc về người đang xem
   * bảng chấm collab, không thuộc về người đang chỉnh khoá API — gom chúng lại
   * chỉ vì cùng là "thiết lập" buộc người dùng nhảy sang một nhóm khác để chỉnh
   * đúng thứ họ đang nhìn.
   */
  readonly setting?: boolean;
  /** Từ khoá phụ cho ô tìm — thứ người ta gõ mà không có trong nhãn. */
  readonly keywords?: string;
}

export interface NavGroup {
  /** Nhóm không tên (mục Tổng quan đứng một mình) để rỗng cả hai thứ tiếng. */
  readonly label: NavLabel;
  /**
   * Đoạn địa chỉ của trang category: `/category/<slug>`.
   *
   * KHAI TAY, không suy từ `label.en`. Suy tự động thì địa chỉ phụ thuộc vào một
   * chuỗi hiển thị, và đổi nhãn tiếng Anh cho hay hơn sẽ âm thầm làm chết một
   * link đã dán vào Discord. Một dòng khai là một quyết định có ý thức.
   *
   * Bỏ trống = nhóm KHÔNG có trang category. Đúng một nhóm như vậy: nhóm không
   * tên chứa mỗi mục Tổng quan — một trang category có đúng một thẻ là một cú
   * bấm chết.
   */
  readonly slug?: string;
  /**
   * Tên một hình trong `components/icons.tsx` — KHÔNG phải emoji, và cũng không
   * phải JSX.
   *
   * Vẫn là CHUỖI vì hai lý do:
   *
   *  • `nav.test.ts` chạy bằng `node --test` trên dữ liệu thuần và không dựng
   *    nổi một cây React. Chú ý là file này `import type` bộ hình, chứ không
   *    import giá trị: `verbatimModuleSyntax` xoá hẳn dòng đó lúc dịch, nên test
   *    không bao giờ chạm tới React. Đổi thành value import là làm hỏng test
   *    frontend duy nhất của cả kho.
   *  • Bản đồ điều hướng là DỮ LIỆU. Nhét JSX vào đây là kéo nó thành một phần
   *    của tầng vẽ, và từ đó không còn đọc được ngoài trình duyệt.
   */
  readonly icon?: IconName;
  /** Một câu nói nhóm này lo việc gì — chữ dưới tiêu đề trang category. */
  readonly description?: NavLabel;
  /**
   * true = nhóm thuộc PHẠM VI CHỦ BOT, không thuộc server nào.
   *
   * Thanh bên hiện đúng MỘT trong hai phạm vi một lúc — hoặc các nhóm của server
   * đang mở, hoặc nhóm này. Xem `ownerScopeOf` ngay dưới về lý do.
   */
  readonly ownerScope?: boolean;
  readonly items: readonly NavItem[];
}

/** Nhóm CÓ trang category — hình dạng sau khi qua `isCategory`. */
export interface NavCategory extends NavGroup {
  readonly slug: string;
  readonly icon: IconName;
  readonly description: NavLabel;
}

/**
 * Menu gom theo CÔNG VIỆC, không theo đường dẫn.
 *
 * Hai thứ đó không trùng nhau, và ép chúng trùng nhau là cách hỏng cả hai. Địa
 * chỉ được dán vào Discord, lưu dấu trang, gửi cho nhau — đổi chúng để menu gọn
 * hơn là làm hỏng những đường dẫn đang chạy để đổi lấy một thứ chỉ mình người
 * ngồi trước menu nhìn thấy. Nên "Câu trả lời được chấp nhận" đứng trong nhóm
 * FAQ (nó là nguyên liệu chính để viết FAQ) dù địa chỉ vẫn là `/community/...`.
 *
 * Thứ tự nhóm theo tần suất mở: xem tình hình → hai mặt trận làm việc hằng ngày
 * (bot tự trả lời, người hỗ trợ) → cộng đồng và phần thưởng → hệ thống, thứ chỉ
 * đụng tới khi cần chỉnh.
 *
 * ── Đợt sắp xếp lại 11/08/2026 ─────────────────────────────────────────────
 *
 * Panel chạm mốc hơn ba mươi trang, và nhóm "Hệ thống" đã thành chỗ đổ: cấu
 * hình collab nằm ở đó trong khi bảng chấm collab nằm ở "Hỗ trợ", bảng vai trò
 * theo cấp độ nằm ở đó trong khi mọi thứ khác về cấp độ nằm ở "Cộng đồng". Quy
 * tắc cũ ngầm là *"cái gì là thiết lập thì về Hệ thống"* — nghe gọn, nhưng nó
 * cắt đôi mọi luồng công việc: người đang xem một bảng phải rời nhóm đó để
 * chỉnh chính bảng đang xem.
 *
 * Quy tắc mới: **trang cấu hình ở lại với thứ nó cấu hình**, đứng cuối nhóm sau
 * một vạch ngăn (`setting: true`). "Hệ thống" chỉ còn giữ đúng thứ thuộc về
 * TIẾN TRÌNH bot chứ không thuộc một mặt công việc nào — khoá API, sao lưu,
 * nhật ký, báo cáo.
 *
 * Không địa chỉ nào đổi trong đợt đó. Xem đoạn ngay trên về vì sao.
 *
 * ── Đợt đổi địa chỉ 12/08/2026 ─────────────────────────────────────────────
 *
 * Toàn bộ đường dẫn chuyển sang tiếng Anh (`/cong-dong/tin-cay` →
 * `/community/trust`). Đây là lần đầu địa chỉ đổi, và nó KHÔNG mâu thuẫn với
 * đoạn trên: cái giá được trả đầy đủ chứ không bỏ qua. Mọi địa chỉ cũ vẫn mở
 * đúng trang qua bảng `LEGACY_PATHS` trong `main.tsx`, giữ nguyên cả `:id` lẫn
 * phần query — nên dấu trang, link dán trong Discord và link trong báo cáo đã
 * gửi đi đều còn nguyên tác dụng.
 *
 * ── Đợt dựng trang category 13/08/2026 ────────────────────────────────────
 *
 * Menu chạm 47 mục và bày phẳng cả 47 cùng lúc. Nhóm giờ có `slug`/`icon`/
 * `description` để trở thành một TRANG thật (`/category/<slug>`), và thanh bên
 * mỗi lúc chỉ mở một nhóm. Vẫn không địa chỉ trang nào đổi — trừ mục Tổng quan
 * chuyển từ `/` sang `/overview`, nhường `/` cho trang chủ. Xem `pages/Home.tsx`.
 *
 * ── Đợt tách nhóm 15/08/2026 ───────────────────────────────────────────────
 *
 * "Cộng đồng" phồng lên 10 mục vì gom chung ba thứ khác hẳn nhau: hệ XP (điểm,
 * bảng xếp hạng, chống farm), việc chào đón thành viên mới, và một trang cấu
 * hình FAQ đi lạc. Tên nhóm không còn đoán được bên trong có gì. Tách ba việc:
 *
 *  • "Cộng đồng" thu lại đúng phạm vi hệ XP — Members & XP, Bảng xếp hạng, Cặp
 *    nghi vấn, Thư điện tử trùng (đều đọc/ghi qua `server/routes/members.ts`)
 *    cộng ba trang cấu hình của chính hệ đó.
 *  • "Chào mừng & vai trò" (nhóm MỚI) — Chào mừng và Nút tự gán vai trò dọn
 *    sang đây: cả hai đều là "thành viên mới nhận được gì", không liên quan gì
 *    tới điểm XP. Không mục nào trong nhóm mới còn `setting: true` — cả nhóm
 *    đã là cấu hình, đánh dấu thêm chỉ tạo một vạch ngăn không có gì đứng trước
 *    nó (giống cách nhóm "Hệ thống" không đánh dấu mục nào).
 *  • "Điểm tin cậy" chuyển sang nhóm "Bot trả lời": trang ghi vào kho cấu hình
 *    chung qua `group: 'asker-trust'`, và việc nó quyết định — trả lời một câu
 *    hỏi đáng bao nhiêu — là việc của hệ trả lời, không phải hệ XP. Nó từng nằm
 *    ở "Cộng đồng" chỉ vì cùng đọc dữ liệu thành viên, không vì cùng việc.
 *
 * "Quản trị" đổi thứ tự chứ không đổi thành viên: Sổ vi phạm (trang ĐỌC, không
 * phải cấu hình) lên đầu — đúng quy tắc "trang cấu hình đứng cuối sau vạch
 * ngăn" mà mọi nhóm khác theo, còn nhóm này trước đó xếp cấu hình lên trước.
 * "Cấu hình quản trị" đổi tên thành "Vai trò & quyền mod" vì nó trùng hệt tên
 * nhóm cha ("Quản trị") — mở nhóm ra thấy một mục tên y hệt nhóm dễ khiến người
 * xem tưởng đó là toàn bộ nhóm.
 */
export const NAV: readonly NavGroup[] = [
  {
    label: { en: '', vi: '' },
    items: [
      {
        to: '/overview',
        label: { en: 'Overview', vi: 'Tổng quan' },
        description: {
          en: 'How the bot and the community are doing right now.',
          vi: 'Tình hình chung của bot và cộng đồng ngay lúc này.',
        },
        keywords: 'dashboard trang chủ',
      },
    ],
  },
  {
    label: { en: 'Monitoring', vi: 'Theo dõi' },
    slug: 'monitoring',
    icon: 'chart-line',
    description: {
      en: 'Numbers and logs — read-only pages you open to see what is going on.',
      vi: 'Số liệu và nhật ký — những trang chỉ để xem tình hình, không chỉnh gì.',
    },
    items: [
      {
        to: '/stats',
        label: { en: 'Server statistics', vi: 'Thống kê máy chủ' },
        description: {
          en: 'Charts over time: questions, answers, languages, token spend.',
          vi: 'Biểu đồ theo thời gian: câu hỏi, câu trả lời, ngôn ngữ, token đã tiêu.',
        },
        keywords: 'biểu đồ số liệu',
      },
      {
        to: '/topic-ranking',
        label: { en: 'Topic ranking', vi: 'Xếp hạng chủ đề' },
        description: {
          en: 'What the community is actually talking about, ranked.',
          vi: 'Cộng đồng đang thực sự nói về cái gì, xếp theo thứ hạng.',
        },
        keywords: 'topic trending',
      },
      // Nhật ký & Tình trạng chuyển từ "Hệ thống" về đây: nó là trang ĐỌC, mở
      // để xem tình hình, không phải để chỉnh gì. Nó cũng là trang duy nhất
      // trong nhóm cũ mà kiểm duyệt viên xem được — nằm lẫn giữa tám mục khoá
      // admin thì với họ cả nhóm đó chỉ có một dòng.
      {
        to: '/logs',
        label: { en: 'Logs & health', vi: 'Nhật ký & Tình trạng' },
        description: {
          en: 'Recent events, warnings and errors from the running bot.',
          vi: 'Sự kiện, cảnh báo và lỗi gần đây của bot đang chạy.',
        },
        keywords: 'log health lỗi sức khoẻ',
      },
    ],
  },
  {
    // Mọi thứ quyết định BOT trả lời cái gì và trả lời thế nào.
    label: { en: 'Bot answers', vi: 'Bot trả lời' },
    slug: 'answers',
    icon: 'chat-dots',
    description: {
      en: 'Everything that decides what the bot answers, and how well.',
      vi: 'Mọi thứ quyết định bot trả lời cái gì và trả lời tốt tới đâu.',
    },
    items: [
      {
        to: '/faq',
        label: { en: 'FAQ entries', vi: 'Danh sách FAQ' },
        description: {
          en: 'The question–answer pairs the bot is allowed to answer from.',
          vi: 'Các cặp hỏi–đáp mà bot được phép lấy ra để trả lời.',
        },
        keywords: 'câu hỏi đáp',
      },
      {
        to: '/faq/suggestions',
        label: { en: 'Pending suggestions', vi: 'Đề xuất chờ duyệt' },
        description: {
          en: 'Draft entries the bot mined from real conversations, waiting for you.',
          vi: 'Mục nháp bot gom được từ hội thoại thật, đang chờ bạn duyệt.',
        },
        keywords: 'suggestion',
      },
      // Bản dịch đứng cạnh FAQ vì nó LÀ FAQ — cùng nội dung đó, bằng ngôn ngữ
      // mà phần lớn người vận hành không đọc được. Xếp nó vào một nhóm "ngôn
      // ngữ" riêng là biến nó thành việc của ai khác, và rồi không ai làm.
      {
        to: '/faq/translations',
        label: { en: 'FAQ translations', vi: 'Bản dịch FAQ' },
        description: {
          en: 'The same answers in the other languages, and what still needs review.',
          vi: 'Cùng câu trả lời đó bằng các ngôn ngữ khác, và phần còn phải duyệt.',
        },
        adminOnly: true,
        keywords: 'dịch ngôn ngữ',
      },
      // Địa chỉ nằm dưới `/cong-dong` vì lý do lịch sử, nhưng công việc thì
      // thuộc về đây: đây là nguồn dữ liệu chính để bổ sung FAQ.
      {
        to: '/community/accepted-answers',
        label: { en: 'Accepted answers', vi: 'Câu trả lời được chấp nhận' },
        description: {
          en: 'Answers a human confirmed — the best raw material for new FAQ entries.',
          vi: 'Câu trả lời đã được người thật xác nhận — nguyên liệu tốt nhất để viết FAQ.',
        },
      },
      {
        to: '/faq/test-retrieval',
        label: { en: 'Test retrieval', vi: 'Thử truy xuất' },
        description: {
          en: 'Type a question and see which entries the bot would pull.',
          vi: 'Gõ một câu hỏi và xem bot sẽ lôi ra những mục nào.',
        },
        keywords: 'test retrieval',
      },
      {
        to: '/faq/health',
        label: { en: 'FAQ health', vi: 'Sức khoẻ FAQ' },
        description: {
          en: 'Duplicates, near-duplicates and entries nobody ever hits.',
          vi: 'Mục trùng, mục gần trùng và mục không ai bao giờ chạm tới.',
        },
        keywords: 'health trùng lặp',
      },
      {
        to: '/topic-filter',
        label: { en: 'Topic filter', vi: 'Bộ lọc chủ đề' },
        description: {
          en: 'Keep off-topic chatter from earning XP.',
          vi: 'Chặn nội dung lạc đề kiếm điểm XP.',
        },
        setting: true,
        keywords: 'topic filter farm',
      },
    ],
  },
  {
    // Mọi thứ về việc NGƯỜI hỗ trợ người. Ticket và collab không tách rời được:
    // bảng chấm collab đọc chính nhật ký ticket, nên để hai nhóm riêng thì
    // người xem phải nhảy qua lại giữa hai chỗ để đối chiếu cùng một câu hỏi.
    label: { en: 'Support & collab', vi: 'Hỗ trợ & collab' },
    slug: 'support',
    icon: 'ticket',
    description: {
      en: 'Everything about people helping people: tickets, and how the team is doing.',
      vi: 'Mọi thứ về việc người giúp người: ticket, và đội hỗ trợ làm việc ra sao.',
    },
    items: [
      {
        to: '/tickets',
        label: { en: 'Ticket overview', vi: 'Tổng hợp ticket' },
        description: {
          en: 'What people open tickets about, and how those tickets go.',
          vi: 'Người ta mở ticket về chuyện gì, và những ticket đó đi tới đâu.',
        },
      },
      {
        to: '/tickets/reviews',
        label: { en: 'Handled tickets', vi: 'Ticket đã hỗ trợ' },
        description: {
          en: 'Tickets handled by other bots, mined for lessons.',
          vi: 'Ticket do bot khác xử lý, đọc lại để rút bài học.',
        },
      },
      // Hai mục dưới đây thuộc tầng bot TỰ tạo ticket, khác hẳn hai mục trên
      // (vốn phân tích ticket của bot khác). Nhãn cố ý nói ra sự khác biệt đó —
      // "Ticket đã hỗ trợ" và "Lịch sử ticket" mà đứng cạnh nhau không chú thích
      // thì không ai đoán được hai bảng khác nhau ở chỗ nào.
      {
        to: '/tickets/panels',
        label: { en: 'Ticket panels', vi: 'Bảng tạo ticket' },
        description: {
          en: 'The button panels and forms members use to open a ticket with this bot.',
          vi: 'Bảng nút và biểu mẫu để thành viên mở ticket với chính bot này.',
        },
        adminOnly: true,
        setting: true,
        keywords: 'create ticket panel form claim close bang tao ticket bieu mau',
      },
      {
        to: '/tickets/history',
        label: { en: 'Ticket history', vi: 'Lịch sử ticket' },
        description: {
          en: 'Closed tickets this bot ran, with the full transcript.',
          vi: 'Ticket do chính bot này chạy và đã đóng, kèm toàn văn hội thoại.',
        },
        keywords: 'transcript history lich su hoi thoai ticket da dong',
      },
      {
        to: '/collab',
        label: { en: 'Collab scorecard', vi: 'Hiệu quả collab' },
        description: {
          en: 'How each collab is doing: attendance, replies, accuracy.',
          vi: 'Từng collab làm việc ra sao: chuyên cần, trả lời, độ chính xác.',
        },
        keywords: 'mod scorecard',
      },
      {
        to: '/collab/actions',
        label: { en: 'Moderation actions', vi: 'Hành động quyền hạn' },
        description: {
          en: 'Who muted, kicked or banned whom — including through other bots.',
          vi: 'Ai đã mute, kick, ban ai — kể cả khi làm qua bot khác.',
        },
        keywords: 'mute kick ban',
      },
      {
        to: '/collab/settings',
        label: { en: 'Collab settings', vi: 'Cấu hình collab' },
        description: {
          en: 'Which role means which country, and where the mod-log channel is.',
          vi: 'Vai trò nào ứng với nước nào, và kênh nhật ký bot mod nằm ở đâu.',
        },
        adminOnly: true,
        setting: true,
      },
    ],
  },
  {
    /**
     * Quản trị server — nhóm RIÊNG, không nhét vào "Hỗ trợ & collab".
     *
     * Nhóm đó nói về việc NGƯỜI giúp người: ticket, chấm hiệu quả collab. Nhóm
     * này là kỷ luật: ai bị cấm, ai bị cảnh cáo, nhật ký nào ghi ở kênh nào.
     * Hai mặt công việc khác nhau, hai người phụ trách khác nhau.
     *
     * Chú thích cũ ghi "còn dài ra ở các đợt sau (tự động lọc nội dung, cổng
     * xác minh, chống raid)" — cả ba đã tới: AutoMod trước đó, Cổng xác minh và
     * Chống raid thêm 15/08/2026.
     */
    label: { en: 'Moderation', vi: 'Quản trị' },
    slug: 'moderation',
    icon: 'shield-check',
    description: {
      en: 'Ban, kick, timeout, warnings — and one separate log channel per kind of event.',
      vi: 'Cấm, đuổi, cấm nói, cảnh cáo — và mỗi loại sự kiện một kênh nhật ký riêng.',
    },
    items: [
      // Trang ĐỌC lên đầu, giống mọi nhóm khác — xem "Đợt tách nhóm 15/08/2026".
      {
        to: '/moderation/infractions',
        label: { en: 'Infractions', vi: 'Sổ vi phạm' },
        description: {
          en: 'Every case the bot recorded, searchable per member.',
          vi: 'Mọi hồ sơ bot đã ghi, tra được theo từng thành viên.',
        },
        adminOnly: true,
        keywords: 'warn ban case history so vi pham canh cao tien an',
      },
      {
        to: '/moderation/verification',
        label: { en: 'Verification gate', vi: 'Cổng xác minh' },
        description: {
          en: 'A button new members click (with an optional question) before they can see the server.',
          vi: 'Thành viên mới bấm một nút (kèm trả lời câu hỏi nếu bật) trước khi thấy được server.',
        },
        adminOnly: true,
        setting: true,
        keywords: 'verification gate xac minh thanh vien moi captcha chong bot',
      },
      {
        to: '/moderation/antiraid',
        label: { en: 'Anti-raid', vi: 'Chống raid' },
        description: {
          en: 'Detect a burst of joins and lock invite links — never kicks or bans on its own.',
          vi: 'Phát hiện join dồn dập và khoá link mời — không bao giờ tự kick/ban.',
        },
        adminOnly: true,
        setting: true,
        keywords: 'anti raid chong raid raid protection lock invite khoa link moi',
      },
      {
        to: '/moderation/antinuke',
        label: { en: 'Anti-nuke', vi: 'Chống phá server' },
        description: {
          en: 'Catch a compromised moderator mass-deleting channels, roles or members — and quarantine them.',
          vi: 'Bắt mod bị chiếm tài khoản xoá hàng loạt kênh, vai trò hay thành viên — và cách ly họ.',
        },
        adminOnly: true,
        setting: true,
        keywords: 'anti nuke chong pha server nuke protection quarantine cach ly mod phan boi bao ve',
      },
      {
        to: '/moderation/automod',
        label: { en: 'AutoMod', vi: 'Kiểm duyệt tự động' },
        description: {
          en: '17 filters that run without anyone watching: flooding, invites, links, banned words, zalgo, lookalikes and more.',
          vi: '17 bộ lọc chạy không cần ai canh: spam, link mời, link ngoài, từ cấm, zalgo, chữ giả dạng và nữa.',
        },
        adminOnly: true,
        setting: true,
        keywords: 'automod spam invite link tu cam caps emoji loc tu dong chan',
      },
      {
        to: '/moderation/autopurge',
        label: { en: 'Scheduled cleanup', vi: 'Dọn kênh theo lịch' },
        description: {
          en: 'Give a channel a retention time and the bot keeps it that short on its own.',
          vi: 'Đặt hạn lưu cho một kênh, bot tự giữ cho kênh đó không dài hơn thế.',
        },
        adminOnly: true,
        setting: true,
        keywords: 'purge don kenh xoa tu dong lich han luu cleanup retention',
      },
      // Kênh nhật ký của bốn trang trên gom về đây 17/08/2026 — Moderation,
      // AutoMod, Dọn kênh theo lịch, Chống raid từng có mỗi trang một khối
      // "Kênh nhật ký" giống hệt nhau về hình dạng. Đứng cuối cùng các trang nó
      // gom lại, đúng quy tắc "trang cấu hình đứng sau vạch ngăn".
      {
        to: '/moderation/logs',
        label: { en: 'Log channels', vi: 'Kênh nhật ký' },
        description: {
          en: 'Every log channel from the four pages above, in one place.',
          vi: 'Mọi kênh nhật ký của bốn trang trên, gom về một chỗ.',
        },
        adminOnly: true,
        setting: true,
        keywords: 'log channel kenh nhat ky audit mod log nhat ky',
      },
      // Đổi tên từ "Moderation settings": trùng hệt tên nhóm cha, xem đợt đổi ở trên.
      // Đổi lại lần nữa 18/08/2026: tên cũ "Vai trò & quyền mod" không nhắc tới
      // DM tuỳ biến (thêm 17/08) nên người dùng lướt qua card này khi tìm tính
      // năng đó — xem thẻ tất cả trừ "quyền" đã có sẵn trong trang này.
      {
        to: '/moderation',
        label: { en: 'Roles, permissions & DM', vi: 'Vai trò, quyền & tin nhắn DM' },
        description: {
          en: 'Turn it on, grant each role only the powers it needs, write the DM sent to warned/muted/kicked/banned members, pick the log channels.',
          vi: 'Bật tính năng, cấp cho mỗi vai trò đúng quyền nó cần, soạn tin DM gửi cho người bị cảnh cáo/cấm nói/đuổi/cấm, chọn kênh nhật ký.',
        },
        adminOnly: true,
        setting: true,
        keywords:
          'ban kick timeout mute warn purge lock slowmode log quan tri canh cao nhat ky dm tin nhan rieng canh bao',
      },
    ],
  },
  {
    /**
     * Tự động hoá — nhóm RIÊNG, không nhét vào "Quản trị".
     *
     * Nhóm kia trả lời "ai bị xử lý và vì sao"; nhóm này trả lời "bot tự nói gì
     * khi không ai gõ lệnh". Chúng trông giống nhau ở chỗ cùng chạy không cần
     * người, nhưng người mở chúng đang làm hai việc khác hẳn: một bên đi tìm một
     * lượt ban, một bên đi sửa một câu chữ. Gộp lại thì nhóm Quản trị — vốn đã
     * dài ra vì AutoMod và Dọn kênh — thành chỗ đổ của mọi thứ có chữ "tự động".
     */
    label: { en: 'Automation', vi: 'Tự động hoá' },
    slug: 'automation',
    icon: 'arrows-clockwise',
    description: {
      en: 'What the bot says on its own: sticky notes, canned replies, scheduled posts.',
      vi: 'Những gì bot tự nói: tin ghim cuối kênh, câu trả lời sẵn, tin theo lịch.',
    },
    items: [
      {
        to: '/automation/sticky',
        label: { en: 'Sticky message', vi: 'Tin ghim cuối kênh' },
        description: {
          en: 'Keep one message at the bottom of a channel — the bot reposts it as people talk.',
          vi: 'Giữ một tin luôn nằm cuối kênh — bot đăng lại mỗi khi có người nói chuyện.',
        },
        adminOnly: true,
        keywords: 'sticky ghim tin cuoi kenh noi quy repost',
      },
      {
        to: '/automation/auto-response',
        label: { en: 'Auto response', vi: 'Trả lời tự động' },
        description: {
          en: 'Reply with a canned message when someone types a keyword.',
          vi: 'Tự trả lời một câu có sẵn khi ai đó gõ đúng từ khoá.',
        },
        adminOnly: true,
        keywords: 'auto response tra loi tu dong tu khoa canned reply',
      },
      {
        to: '/automation/auto-message',
        label: { en: 'Scheduled message', vi: 'Tin nhắn theo lịch' },
        description: {
          en: 'Send a message automatically on chosen days and times.',
          vi: 'Tự gửi một tin nhắn vào đúng ngày, đúng giờ đã chọn.',
        },
        adminOnly: true,
        keywords: 'auto message scheduled tin nhan theo lich hen gio',
      },
      {
        to: '/automation/dm',
        label: { en: 'Direct message', vi: 'DM riêng' },
        description: { en: 'Send a private message when members join, open a ticket, or chat.', vi: 'Gửi tin riêng khi thành viên vào server, tạo ticket hoặc tương tác.' },
        adminOnly: true,
        allowedUserIds: [DIRECT_MESSAGE_ADMIN_ID],
        keywords: 'dm direct message tin nhan rieng chao mung blacklist cooldown',
      },
      {
        to: '/automation/broadcast',
        label: { en: 'Send message', vi: 'Gửi tin nhắn' },
        description: {
          en: 'Compose text and an optional embed, send it to a channel right now — and edit it later.',
          vi: 'Soạn chữ và embed tuỳ chọn, gửi vào một kênh ngay lập tức — và sửa lại được sau đó.',
        },
        adminOnly: true,
        keywords: 'embed broadcast send message gui tin nhan embed thong bao',
      },
      {
        to: '/automation/voice-rooms',
        label: { en: 'Join to Create', vi: 'Kênh voice tạm' },
        description: {
          en: 'A member joins an empty voice channel and the bot builds them their own room.',
          vi: 'Thành viên vào một kênh voice trống, bot tự dựng cho họ một phòng riêng.',
        },
        adminOnly: true,
        keywords: 'voice room join to create jtc kenh voice tam phong tam',
      },
    ],
  },
  {
    label: { en: 'Community', vi: 'Cộng đồng' },
    slug: 'community',
    icon: 'users-three',
    description: {
      en: 'Member XP, levels and the leaderboard — plus the anti-farm checks that keep it honest.',
      vi: 'Điểm, cấp độ và bảng xếp hạng của thành viên — cùng các phép chống farm giữ hệ thống trung thực.',
    },
    items: [
      {
        to: '/community/members',
        label: { en: 'Members & XP', vi: 'Thành viên & XP' },
        description: {
          en: 'Every member, their points, and where those points came from.',
          vi: 'Từng thành viên, điểm của họ, và điểm đó đến từ đâu.',
        },
        keywords: 'member điểm',
      },
      {
        to: '/community/leaderboard',
        label: { en: 'Leaderboard', vi: 'Bảng xếp hạng' },
        description: {
          en: 'Who is on top this season, and by how much.',
          vi: 'Ai đang dẫn đầu mùa này, và hơn bao nhiêu.',
        },
        keywords: 'leaderboard top',
      },
      {
        to: '/community/suspicious-pairs',
        label: { en: 'Suspicious pairs', vi: 'Cặp nghi vấn' },
        description: {
          en: 'Two accounts feeding each other points — the usual shape of XP farming.',
          vi: 'Hai tài khoản bơm điểm cho nhau — dáng thường thấy của việc farm XP.',
        },
        keywords: 'gian lận farm',
      },
      {
        to: '/community/duplicate-emails',
        label: { en: 'Duplicate emails', vi: 'Thư điện tử trùng lặp' },
        description: {
          en: 'One address registered by several accounts.',
          vi: 'Một địa chỉ được nhiều tài khoản cùng đăng ký.',
        },
        keywords: 'email',
      },
      // "Điểm tin cậy" quay lại đây trong đợt 16/08/2026: nó là cơ chế XP —
      // hệ số nhân XP theo mức hoạt động của người hỏi, chống farm bằng tài
      // khoản phụ — nên thuộc phạm vi hệ XP, không phải hệ trả lời. Trang từng
      // dọn sang "Bot trả lời" ngày 15/08/2026 vì đọc `group: 'asker-trust'`
      // trong kho cấu hình chung, nhưng CÁI GHI vào đâu không quyết định việc
      // NÓ LÀ GÌ — chín tham số đó đều là luật tính XP.
      {
        to: '/community/trust',
        label: { en: 'Asker trust', vi: 'Điểm tin cậy' },
        description: {
          en: 'How much a question is worth answering, by who is asking.',
          vi: 'Trả lời một câu hỏi đáng bao nhiêu, tuỳ vào người hỏi là ai.',
        },
        adminOnly: true,
        setting: true,
      },
      // Hai trang cấu hình của chính hệ XP, giờ đứng cạnh dữ liệu XP thay vì
      // nằm rải giữa hai nhóm khác nhau. ("Chào mừng" dọn đi nơi khác trong
      // đợt 15/08/2026 — xem chú thích đầu file.)
      {
        to: '/community/xp-tuning',
        label: { en: 'XP tuning', vi: 'Tinh chỉnh XP' },
        description: {
          en: 'What earns points, how much, and the anti-farm limits.',
          vi: 'Việc gì được điểm, được bao nhiêu, và các mức chặn farm.',
        },
        adminOnly: true,
        setting: true,
      },
      {
        to: '/community/level-roles',
        label: { en: 'Level roles', vi: 'Vai trò theo cấp độ' },
        description: {
          en: 'Roles the bot hands out automatically as members level up.',
          vi: 'Vai trò bot tự phát khi thành viên lên cấp.',
        },
        adminOnly: true,
        setting: true,
      },
      {
        to: '/community/countries',
        label: { en: 'Countries by role', vi: 'Quốc gia theo vai trò' },
        description: {
          en: 'Which role means which country, for per-country reporting.',
          vi: 'Vai trò nào ứng với nước nào, để báo cáo tách theo nước.',
        },
        adminOnly: true,
        setting: true,
      },
    ],
  },
  {
    /**
     * Nhóm MỚI (đợt 15/08/2026) — tách ra khỏi "Cộng đồng", xem chú thích đầu file.
     *
     * Hai mục ở đây trả lời cùng một câu hỏi: "thành viên mới/thành viên hiện
     * tại nhận vai trò bằng cách nào". Không mục nào đánh dấu `setting: true`
     * — cả nhóm đã là cấu hình thuần, giống nhóm "Hệ thống".
     */
    label: { en: 'Welcome & roles', vi: 'Chào mừng & vai trò' },
    slug: 'welcome-roles',
    icon: 'id-badge',
    description: {
      en: 'What happens when someone joins, and how members get a role afterward.',
      vi: 'Chuyện gì xảy ra khi có người vào, và sau đó thành viên nhận vai trò bằng cách nào.',
    },
    items: [
      {
        to: '/community/welcome',
        label: { en: 'Welcome', vi: 'Chào mừng' },
        description: {
          en: 'What happens when somebody joins — message, image card, DM, autorole.',
          vi: 'Có người vào thì làm gì — tin chào, thẻ ảnh, nhắn riêng, tự gắn vai trò.',
        },
        adminOnly: true,
        keywords: 'welcome join leave autorole chao mung thanh vien moi tam biet anh',
      },
      {
        to: '/community/reaction-roles',
        label: { en: 'Role buttons', vi: 'Nút tự gán vai trò' },
        description: {
          en: 'A message with buttons — members click to give themselves a role.',
          vi: 'Một tin nhắn có nút — thành viên bấm để tự nhận một vai trò.',
        },
        adminOnly: true,
        keywords: 'reaction role self role button nut tu gan vai tro',
      },
      {
        to: '/community/giveaways',
        label: { en: 'Giveaways', vi: 'Giveaway' },
        description: {
          en: 'Post a message with an Enter button, draw winners automatically when time is up.',
          vi: 'Đăng tin có nút Tham gia, tự quay số người thắng khi hết giờ.',
        },
        keywords: 'giveaway prize draw winner qua tang trung thuong quay so',
      },
    ],
  },
  {
    // Thứ tự trong nhóm này theo VÒNG ĐỜI một phần thưởng: khai nó là gì → khai
    // ai được nhận → duyệt → phát → nguồn hàng → hai công cụ chạy theo chu kỳ.
    label: { en: 'Rewards', vi: 'Phần thưởng' },
    slug: 'rewards',
    icon: 'gift',
    description: {
      en: 'The whole life of a reward: declare it, approve it, hand it out, close the cycle.',
      vi: 'Trọn vòng đời một phần thưởng: khai ra, duyệt, phát, rồi chốt chu kỳ.',
    },
    items: [
      {
        to: '/rewards/catalog',
        label: { en: 'Reward catalog', vi: 'Danh mục phần thưởng' },
        description: {
          en: 'What can be won, and what each item costs you.',
          vi: 'Có thể nhận được những gì, và mỗi thứ tốn của bạn bao nhiêu.',
        },
        adminOnly: true,
      },
      {
        to: '/rewards/milestones',
        label: { en: 'Personal milestones', vi: 'Mốc cá nhân' },
        description: {
          en: 'One-off prizes for reaching a level or keeping a streak.',
          vi: 'Thưởng một lần khi đạt cấp độ hoặc giữ được chuỗi ngày.',
        },
        adminOnly: true,
        keywords: 'milestone cấp độ streak',
      },
      {
        to: '/rewards/pending',
        label: { en: 'Pending approval', vi: 'Chờ duyệt' },
        description: {
          en: 'Awards waiting for a human to say yes.',
          vi: 'Lượt thưởng đang chờ một người thật gật đầu.',
        },
        adminOnly: true,
      },
      {
        to: '/rewards/queue',
        label: { en: 'Delivery queue', vi: 'Hàng đợi cấp phát' },
        description: {
          en: 'Approved awards still to be handed over.',
          vi: 'Lượt thưởng đã duyệt nhưng chưa trao tới tay.',
        },
        adminOnly: true,
      },
      {
        to: '/rewards/grant',
        label: { en: 'Grant & revoke', vi: 'Trao tay & thu hồi' },
        description: {
          en: 'Give something outside the normal rules, or take it back.',
          vi: 'Trao một thứ ngoài luật thường, hoặc thu lại.',
        },
        adminOnly: true,
      },
      {
        to: '/rewards/codes',
        label: { en: 'Code pools', vi: 'Kho mã giảm giá' },
        description: {
          en: 'The discount codes the bot draws from when it pays out.',
          vi: 'Kho mã giảm giá bot rút ra mỗi lần trao thưởng.',
        },
        adminOnly: true,
        keywords: 'code',
      },
      {
        to: '/rewards/raffle',
        label: { en: 'Raffle draw', vi: 'Quay số' },
        description: {
          en: 'Run the draw — the seed is published first so anyone can check it.',
          vi: 'Quay số — hạt giống công bố trước để ai cũng kiểm lại được.',
        },
        adminOnly: true,
        keywords: 'raffle xổ số',
      },
      {
        to: '/rewards/cycle-preview',
        label: { en: 'Cycle close preview', vi: 'Xem trước chốt chu kỳ' },
        description: {
          en: 'What closing the cycle would pay out, before you commit to it.',
          vi: 'Chốt chu kỳ sẽ phải trả những gì, xem trước khi bấm thật.',
        },
        adminOnly: true,
      },
      // Cơ cấu giải tuần/tháng, xổ số, thưởng sàn — trước đây nằm trong trang
      // Cấu hình chung ở nhóm Hệ thống, tức cách mọi trang phần thưởng khác
      // bảy nhóm. Xem `configCatalog.ts`, kiểu `CatalogSection`.
      {
        to: '/rewards/settings',
        label: { en: 'Reward settings', vi: 'Cấu hình phần thưởng' },
        description: {
          en: 'Weekly and monthly prize structure, raffle rules, floor awards.',
          vi: 'Cơ cấu giải tuần và tháng, luật xổ số, thưởng sàn.',
        },
        adminOnly: true,
        setting: true,
        keywords: 'giai thuong tuan thang xo so raffle co cau',
      },
    ],
  },
  {
    // Chỉ còn thứ thuộc về TIẾN TRÌNH bot, không thuộc một mặt công việc nào.
    label: { en: 'System', vi: 'Hệ thống' },
    slug: 'system',
    icon: 'gear-six',
    description: {
      en: 'The bot process itself — access, API keys, backups. Nothing about day-to-day work.',
      vi: 'Bản thân tiến trình bot — quyền vào, khoá API, sao lưu. Không dính việc hằng ngày.',
    },
    items: [
      {
        to: '/settings',
        label: { en: 'Settings', vi: 'Cấu hình' },
        description: {
          en: 'Channels, thresholds and switches that do not belong to one feature.',
          vi: 'Kênh, ngưỡng và công tắc không thuộc riêng tính năng nào.',
        },
        adminOnly: true,
        keywords: 'thiết lập settings',
      },
      // Quyền vào panel đứng TRƯỚC khoá AI: nó là thứ người vận hành một cộng
      // đồng mới cần trước tiên — thêm đội của mình vào — và trước lượt này họ
      // không có cách nào làm ngoài việc trao quyền Administrator của Discord.
      {
        to: '/panel-access',
        label: { en: 'Panel access', vi: 'Quyền vào panel' },
        description: {
          en: 'Which Discord roles can open this panel, and how much they see.',
          vi: 'Vai trò Discord nào mở được panel này, và xem được tới đâu.',
        },
        adminOnly: true,
        keywords: 'role quyen admin mod permission access vai tro',
      },
      {
        to: '/ai-keys',
        label: { en: 'AI keys', vi: 'Khoá AI' },
        description: {
          en: 'Your own provider keys, their token budget, and what is left.',
          vi: 'Khoá nhà cung cấp của riêng bạn, hạn mức token và phần còn lại.',
        },
        adminOnly: true,
        keywords: 'api key token',
      },
      {
        to: '/backups',
        label: { en: 'Backup & restore', vi: 'Sao lưu & khôi phục' },
        description: {
          en: 'Snapshot this server’s data, download it, or put it back.',
          vi: 'Chụp lại dữ liệu server này, tải về, hoặc đưa về bản đã chụp.',
        },
        adminOnly: true,
        keywords: 'backup restore',
      },
    ],
  },
  {
    /**
     * Nhóm CHỦ BOT — mọi mục nhìn ngang qua tất cả cộng đồng cùng lúc.
     *
     * Đứng CUỐI và mang `ownerOnly` cho từng mục: panel này phục vụ quản trị viên
     * của nhiều cộng đồng khác nhau, và với họ cả nhóm không tồn tại. Gom lại
     * thành một nhóm riêng thay vì rải vào "Hệ thống" vì chúng khác nhau ở một
     * điều cơ bản — mọi mục khác trong panel nói về SERVER ĐANG CHỌN, còn ở đây
     * ô chọn server ở góc trái không có nghĩa gì cả.
     *
     * Trước 12/08/2026 nhóm việc này gác bằng "admin của server neo", tức là nó
     * nằm lẫn trong panel của một cộng đồng cụ thể. Xem `routes/owner.ts`.
     */
    label: { en: 'Bot owner', vi: 'Chủ bot' },
    slug: 'owner',
    icon: 'crown',
    description: {
      en: 'Across every server at once — this scope has no “current server”.',
      vi: 'Nhìn ngang qua mọi server cùng lúc — phạm vi này không có "server đang chọn".',
    },
    ownerScope: true,
    items: [
      {
        to: '/owner',
        label: { en: 'Owner console', vi: 'Tổng quan chủ bot' },
        description: {
          en: 'Every server the bot serves, side by side.',
          vi: 'Mọi server bot đang phục vụ, đặt cạnh nhau.',
        },
        ownerOnly: true,
        keywords: 'owner tong quan thong ke server member token overview',
      },
      {
        to: '/owner/broadcast',
        label: { en: 'Send to every server', vi: 'Gửi tin toàn server' },
        description: {
          en: 'Compose one embed, send it to every server at once — with each server\'s admin role tagged.',
          vi: 'Soạn một tin embed, gửi tới mọi server cùng lúc — kèm tag role admin của từng server.',
        },
        ownerOnly: true,
        keywords: 'broadcast embed tag admin gui tin toan server thong bao hang loat fleet',
      },
      // Nhật ký tiến trình đứng TRƯỚC sao lưu: nó là thứ người ta mở khi có
      // chuyện, còn sao lưu là thứ mở theo lịch. Menu nên xếp theo mức độ khẩn.
      {
        to: '/owner/logs',
        label: { en: 'Process logs', vi: 'Nhật ký tiến trình' },
        description: {
          en: 'Raw console output of the running process — what you open when something breaks.',
          vi: 'Nhật ký thô của tiến trình đang chạy — thứ mở ra khi có chuyện.',
        },
        ownerOnly: true,
        keywords: 'log console stdout stderr loi error crash vps terminal nhat ky',
      },
      {
        to: '/owner/backups',
        label: { en: 'Backup — every server', vi: 'Sao lưu toàn bộ server' },
        description: {
          en: 'One snapshot covering the whole installation.',
          vi: 'Một bản chụp cho toàn bộ bản cài.',
        },
        ownerOnly: true,
        keywords: 'backup restore sao luu khoi phuc toan bo he thong all',
      },
      {
        to: '/owner/defaults',
        label: { en: 'Default settings', vi: 'Mặc định chung' },
        description: {
          en: 'What a brand-new server starts with before anyone configures it.',
          vi: 'Server mới toanh bắt đầu với những gì, trước khi ai cấu hình.',
        },
        ownerOnly: true,
        setting: true,
        keywords: 'default mac dinh chung moi server global',
      },
      {
        // Đổi tên và mô tả 16/08/2026: trang không còn là "chỗ cho mượn khoá"
        // thuần tuý — dải sức khoẻ đội hình đứng đầu trang, số token dùng theo
        // model chuyển từ /owner sang đây. Xem chú thích đầu `pages/SharedAiKeys.tsx`.
        to: '/shared-ai-keys',
        label: { en: 'AI keys & spend', vi: 'Khoá AI & chi tiêu' },
        description: {
          en: 'Fleet health across every server, keys you lend out, and the spend ceiling per server.',
          vi: 'Sức khoẻ đội hình mọi server, khoá bạn cho mượn, và trần chi tiêu cho từng server.',
        },
        ownerOnly: true,
        setting: true,
        keywords: 'api key dung thu trial chung mac dinh operator owner tran rieng ngoai le chan block cap khoa cho server suc khoe health fleet token chi tieu spend',
      },
    ],
  },
];

/**
 * Bốn trang mở hằng ngày — lối tắt trên thẻ "Tiếp tục" ở trang chủ.
 * Khai bằng ĐỊA CHỈ rồi giải ra `NavItem`, để nhãn không có bản chép thứ hai.
 * `nav.test.ts` chốt cả bốn còn tồn tại và không mục nào khoá theo quyền.
 */
export const HOME_SHORTCUTS: readonly NavItem[] = ['/overview', '/faq/suggestions', '/tickets', '/logs']
  .flatMap((to) => NAV.flatMap((group) => group.items).filter((item) => item.to === to));

/** Nhóm này có trang category không. */
export function isCategory(group: NavGroup): group is NavCategory {
  return (
    typeof group.slug === 'string' &&
    group.slug.length > 0 &&
    typeof group.icon === 'string' &&
    group.description !== undefined
  );
}

/** Mọi nhóm CÓ trang category, theo đúng thứ tự của `NAV`. */
export const CATEGORIES: readonly NavCategory[] = NAV.filter(isCategory);

/** Đoạn đầu của mọi địa chỉ trang category. */
export const CATEGORY_PREFIX = '/category';

/**
 * Category ứng với một địa chỉ. `null` = địa chỉ không thuộc category nào
 * (trang chủ, Tổng quan, trang lạ).
 *
 * ── Vì sao SUY RA TỪ ĐỊA CHỈ, không phải một state riêng ────────────────────
 *
 * Thanh bên mỗi lúc mở đúng một nhóm. Cách hiển nhiên là một biến `openCategory`
 * đặt trong `onClick` — và cách đó sai ở đúng chỗ khó thấy nhất: nó tạo ra được
 * trạng thái mà menu nói một đằng còn trang đang mở nói một nẻo. Dán thẳng
 * `/rewards/queue` vào thanh địa chỉ trong lúc `openCategory` là `null` thì menu
 * vẽ chín nhóm đóng, không nhóm nào sáng, và người dùng đứng giữa một trang mà
 * thanh bên khẳng định là không tồn tại.
 *
 * Suy từ `pathname` thì hai thứ đó không thể lệch nhau: chúng là MỘT dữ kiện.
 * Không có gì phải lưu, phải đồng bộ, hay phải khôi phục sau khi nạp lại — và
 * nhờ vậy cả cơ chế nhớ-nhóm-đã-thu trong localStorage của bản trước biến mất.
 */
export function categoryOfPath(pathname: string): NavCategory | null {
  if (pathname === CATEGORY_PREFIX || pathname.startsWith(`${CATEGORY_PREFIX}/`)) {
    const slug = pathname.slice(CATEGORY_PREFIX.length + 1);
    return CATEGORIES.find((category) => category.slug === slug) ?? null;
  }

  const active = activeNavPath(pathname);
  if (active === '') return null;
  return CATEGORIES.find((category) => category.items.some((item) => item.to === active)) ?? null;
}

/**
 * Địa chỉ này có thuộc khu chủ bot không.
 *
 * Suy qua `categoryOfPath` chứ không qua một danh sách địa chỉ riêng, và đó là
 * điểm khác với bản trước: bản đó liệt kê `item.to` của nhóm chủ bot, nên
 * `/category/owner` — một địa chỉ THUỘC khu đó nhưng không phải mục nào — rơi
 * vào phạm vi server và vẽ ra thanh bên của một cộng đồng cụ thể. Đúng kiểu
 * lệch mà `categoryOfPath` sinh ra để chặn.
 */
export function ownerScopeOf(pathname: string): boolean {
  return categoryOfPath(pathname)?.ownerScope === true;
}

/**
 * Mục đang mở = mục KHỚP DÀI NHẤT với địa chỉ hiện tại.
 *
 * `NavLink` mặc định sáng mọi mục là tiền tố của địa chỉ, nên đứng ở
 * `/tickets/reviews` thì cả "Tổng hợp ticket" lẫn "Ticket đã hỗ trợ" cùng sáng,
 * và người xem không biết mình đang ở đâu. Bật `end` cho mục cha thì hỏng theo
 * chiều ngược lại: mở trình soạn `/faq/new` sẽ không mục nào sáng, và menu trông
 * như vừa rơi ra khỏi trang.
 *
 * Luật "cụ thể nhất thắng" xử lý cả hai: `/faq/new` sáng ở "Danh sách FAQ" (chỉ
 * `/faq` khớp), còn `/faq/suggestions` sáng ở đúng mục của nó.
 */
export function activeNavPath(pathname: string): string {
  let best = '';
  for (const group of NAV) {
    for (const item of group.items) {
      const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && item.to.length > best.length) best = item.to;
    }
  }
  return best;
}

/**
 * Bỏ dấu tiếng Việt để ô tìm khớp cả khi gõ không dấu.
 *
 * "phan thuong" phải ra "Phần thưởng". Người vận hành gõ nhanh và gõ không dấu
 * là chuyện thường; một ô tìm chỉ khớp đúng chính tả có dấu thì với phần lớn
 * lượt gõ nó im lặng trả về rỗng, và người ta thôi dùng nó sau hai lần.
 */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase();
}
